// The app's line to the desktop stream frame. The agent-injected bridge inside the frame
// takes these messages (only from its parent, on the app origin) and turns them into keys,
// typed text and pastes for the focused Linux app. It reports the machine clipboard back.

import { store, toast } from '../store';

let frame: HTMLIFrameElement | null = null;
let machineClip = '';

export function setDesktopFrame(f: HTMLIFrameElement | null) {
  frame = f;
}

function post(msg: unknown) {
  frame?.contentWindow?.postMessage(msg, '*');
}

export type DesktopKey = { key: string; code: string; ctrl?: boolean };

export const desktopKeys = (keys: DesktopKey[]) => post({ type: 'cmx-keys', keys });
export const desktopText = (text: string, ctrl = false) => post({ type: 'cmx-text', text, ctrl });
/** Paste text into the focused desktop app (via the machine clipboard + Ctrl+V). */
export const desktopPaste = (text: string) => post({ type: 'cmx-paste', text });

/** The last text copied on the machine (as reported by the stream client), or ''. */
export const machineClipboard = () => machineClip;

let pressedAt = 0;
/** When the person last pressed in the stream (a click or tap on the desktop). */
export const framePressedAt = () => pressedAt;

const pressListeners = new Set<() => void>();
/** Calls `f` on each press in the stream (not when the stream just takes the focus). */
export function onFramePress(f: () => void): () => void {
  pressListeners.add(f);
  return () => pressListeners.delete(f);
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', (e) => {
    if (!frame || e.source !== frame.contentWindow) return;
    const d = e.data as { type?: string; text?: unknown; compat?: unknown; x?: unknown; y?: unknown; w?: unknown; h?: unknown } | null;
    if (d?.type === 'cmx-clip' && typeof d.text === 'string') machineClip = d.text;
    if (d?.type === 'cmx-pointer') {
      pressedAt = Date.now();
      pressListeners.forEach((f) => f());
    }
    if (d?.type === 'cmx-view' && [d.x, d.y, d.w, d.h].every((v) => typeof v === 'number'))
      store.set({ desktopView: { x: d.x as number, y: d.y as number, w: d.w as number, h: d.h as number } });
    // The network blocks UDP, so the stream fell back from WebRTC to WebSockets.
    if (d?.type === 'cmx-stream' && d.compat === true)
      toast({ title: 'Desktop in compatibility mode', body: 'This network blocks the fast stream, so there’s more lag.' }, 5000);
  });
}
